import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * Paging reads as a sentence, not a row of numbers: "‹ Seite 1 von 3 ›", body
 * size, chevrons in `text` and the unavailable one in `text-disabled`.
 *
 * The chevrons are disabled at the ends rather than hidden, so the control
 * keeps its width and the sentence does not jump as pages turn. The step is
 * clamped as well: a caller that re-renders with a smaller `pageCount` cannot
 * be asked for a page that no longer exists.
 */
export interface FxPagerProps extends React.HTMLAttributes<HTMLDivElement> {
  page: number
  pageCount: number
  onPageChanged?: (page: number) => void
}

export function FxPager({ page, pageCount, onPageChanged, className, ...props }: FxPagerProps) {
  function go(to: number) {
    if (to < 1 || to > pageCount || to === page) return
    onPageChanged?.(to)
  }

  const step =
    'inline-flex rounded-sm p-0.5 text-text disabled:cursor-default disabled:text-text-disabled'

  return (
    <div className={cn('inline-flex items-center gap-1 text-body', className)} {...props}>
      <button
        type="button"
        className={step}
        disabled={page <= 1}
        aria-label="Vorherige Seite"
        onClick={() => go(page - 1)}
      >
        <FxIcon name="chevron_left" size={22} />
      </button>
      <span>{`Seite ${page} von ${pageCount}`}</span>
      <button
        type="button"
        className={step}
        disabled={page >= pageCount}
        aria-label="Nächste Seite"
        onClick={() => go(page + 1)}
      >
        <FxIcon name="chevron_right" size={22} />
      </button>
    </div>
  )
}
